import React, { useState, createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";

import { Modal, Backdrop, Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { customToast as toast } from "../utils";
import "./ModalContext.scss";
import { style, styleBackdrop } from "./variables";

const initialState = {
  isModalOpen: false,
  modalContent: "",
};

export const ModalContext = createContext(initialState);

export function ModalConsumer({ theme }) {
  const handleModalClick = event => {
    event.stopPropagation();
  };

  return (
    <ModalContext.Consumer>
      {({
        isModalOpen,
        modalContent,
        setModalOpen,
        msg,
        showMessage,
        showCloseButton,
      }) => (
        <Backdrop
          color="transparent"
          open={isModalOpen}
          sx={styleBackdrop}
          onClick={() => setModalOpen(false)}
        >
          <Modal
            open={isModalOpen}
            onClick={handleModalClick}
            onClose={() => {
              showMessage && toast.error(msg);
              setModalOpen(false);
            }}
          >
            <Box
              bgcolor={
                theme === "light"
                  ? "rgba(246, 249, 254, 0.8)"
                  : "rgba(9, 25, 40, 0.65)"
              }
              color={theme === "light" ? "black" : "white"}
              sx={style}
            >
              <Box
                className={`${theme === "light" ? "light_modal" : "dark_modal"}`}
                sx={{
                  maxHeight: "90vh",
                  overflow: "auto",
                }}
              >
                {showCloseButton && (
                  <IconButton
                    aria-label="cerrar"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      zIndex: 100,
                    }}
                    onClick={() => setModalOpen(false)}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
                {modalContent}
              </Box>
            </Box>
          </Modal>
        </Backdrop>
      )}
    </ModalContext.Consumer>
  );
}

ModalConsumer.propTypes = {};

export function ModalProvider({ children, theme }) {
  const [state, setState] = useState(initialState);

  const setModalOpen = (
    isModalOpen,
    modalContent,
    msg = "",
    showMessage = false,
    showCloseButton = false,
  ) => {
    setState({ isModalOpen, modalContent, msg, showMessage, showCloseButton });
  };

  const valueToReturn = useMemo(() => ({ ...state, setModalOpen }), [state]);

  return (
    <ModalContext.Provider value={valueToReturn}>
      {children}
      <ModalConsumer theme={theme} />
    </ModalContext.Provider>
  );
}

ModalProvider.propTypes = {
  children: PropTypes.node,
  theme: PropTypes.string,
};

ModalProvider.defaultProps = {
  children: null,
  theme: "",
};

ModalConsumer.propTypes = {
  theme: PropTypes.string,
};

ModalConsumer.defaultProps = {
  theme: "",
};

export const useModal = () => useContext(ModalContext);

export default ModalContext;
